/** Titles and "versus" never end a sentence. */
const TITLE_ABBR = new Set(["dr", "mr", "mrs", "ms", "prof", "sr", "jr", "st", "vs", "mt", "gen", "gov", "col", "lt", "capt", "rev", "hon"]);
/** Other abbreviations: the sentence continues unless the next word starts with a capital letter. */
const ABBR = new Set([
  "etc", "inc", "ltd", "co", "no", "e.g", "i.e", "u.s", "u.k", "u.n", "a.m", "p.m", "approx", "dept", "fig", "est", "ave", "blvd",
  "jan", "feb", "mar", "apr", "jun", "jul", "aug", "sep", "sept", "oct", "nov", "dec",
]);
const MONTH_ABBR = new Set(["jan", "feb", "mar", "apr", "jun", "jul", "aug", "sep", "sept", "oct", "nov", "dec"]);

export function splitSentences(text: string): string[] {
  const parts: string[] = [];
  const lines = text.split(/\n+/).map(l => l.trim()).filter(Boolean); // each line ends a sentence
  for (const line of lines) {
    let start = 0;
    const re = /[.!?]+["')\]”’]*(?=\s|$)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(line))) {
      const end = m.index + m[0].length;
      if (m[0].startsWith(".") && !/^\.{2,}/.test(m[0])) {
        const before = line.slice(start, m.index);
        const word = (before.match(/(?<![A-Za-z.])([A-Za-z]+(?:\.[A-Za-z]+)*)$/)?.[1] ?? "").toLowerCase();
        const next = line.slice(end).trimStart();
        if (word) {
          if (TITLE_ABBR.has(word) || /^[a-z]$/.test(word)) continue;
          if (ABBR.has(word)) {
            if (MONTH_ABBR.has(word) && /^\d/.test(next)) continue;
            if (next && !/^[A-Z]/.test(next)) continue;
          }
        }
      }
      const piece = line.slice(start, end).trim();
      if (piece) parts.push(piece);
      start = end;
    }
    const rest = line.slice(start).trim();
    if (rest) parts.push(rest);
  }
  return parts;
}

export function countSyllables(word: string): number {
  let w = word.toLowerCase().replace(/[^a-z]/g, "");
  if (!w) return 0;
  if (w.length <= 3) return 1;
  w = w.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, "").replace(/^y/, "");
  return w.match(/[aeiouy]{1,2}/g)?.length || 1;
}

const STOP = new Set("the of and to a in is that it for was on are as with his they be at one have this from or had by not but what all were we when your can said there use an each which she do how their if will up other about out many then them these so some her would make like him into time has look two more write go see number no way could people my than first been who its now find long down day did get come made may part".split(" "));

export type Readability = ReturnType<typeof analyzeText>;

export function gradeLabel(g: number): string {
  if (g < 1) return "Kindergarten";
  if (g >= 16) return "College graduate (16+)";
  const n = Math.round(g);
  if (n >= 13) return "College";
  return `Grade ${Math.max(1, n)}`;
}

export function easeLabel(f: number): string {
  if (f >= 90) return "Very easy";
  if (f >= 80) return "Easy";
  if (f >= 70) return "Fairly easy";
  if (f >= 60) return "Plain English";
  if (f >= 50) return "Fairly difficult";
  if (f >= 30) return "Difficult";
  return "Very difficult";
}

export function analyzeText(text: string) {
  const sentences = splitSentences(text);
  const clean = text.replace(/https?:\/\/\S+|www\.\S+/g, " ");
  const words = clean.match(new RegExp("\\p{L}[\\p{L}'’-]*", "gu")) ?? [];
  const letters = (clean.match(new RegExp("\\p{L}", "gu")) ?? []).length;
  const latin = (clean.match(/[A-Za-zÀ-ɏ]/g) ?? []).length;
  const lower = words.map(w => w.toLowerCase());
  const stopHits = lower.filter(w => STOP.has(w)).length;
  let language: "ok" | "non-latin" | "maybe-other" = "ok";
  if (letters > 0 && latin / letters < 0.7) language = "non-latin";
  else if (words.length >= 20 && stopHits / words.length < 0.12) language = "maybe-other";

  const wc = words.length || 1;
  const sc = sentences.length || 1;
  const syl = words.map(countSyllables);
  const syllables = syl.reduce((a, b) => a + b, 0);
  const complex = words.filter((_, i) => syl[i] >= 3);
  const chars = words.reduce((a, w) => a + w.replace(/[^A-Za-z0-9À-ɏ]/g, "").length, 0);

  const wps = wc / sc;
  const spw = syllables / wc;
  const flesch = Math.max(0, Math.min(100, 206.835 - 1.015 * wps - 84.6 * spw));
  const fk = Math.max(0, 0.39 * wps + 11.8 * spw - 15.59);
  const fog = Math.max(0, 0.4 * (wps + 100 * (complex.length / wc)));
  const smog = sc >= 3 ? Math.max(0, 1.043 * Math.sqrt(complex.length * (30 / sc)) + 3.1291) : null;
  const L = (chars / wc) * 100;
  const S = (sc / wc) * 100;
  const cli = Math.max(0, 0.0588 * L - 0.296 * S - 15.8);
  const ari = Math.max(0, 4.71 * (chars / wc) + 0.5 * wps - 21.43);

  const longSentences = sentences.filter(s => (s.match(/\S+/g) ?? []).length > 25);
  const hardWords = Array.from(new Set(complex.map(w => w.toLowerCase()))).slice(0, 40);
  const consensus = [fk, fog, cli, ari, ...(smog === null ? [] : [smog])];
  const avgGrade = consensus.reduce((a, b) => a + b, 0) / consensus.length;

  return {
    words: words.length, sentences: sentences.length, syllables, complexCount: complex.length,
    flesch, fk, fog, smog, cli, ari, avgGrade, longSentences, hardWords, language,
    sentenceList: sentences,
  };
}
